import React from "react";
import "suneditor/dist/css/suneditor.min.css";
import SunEditor from "suneditor-react";
import {
  align,
  font,
  fontColor,
  fontSize,
  formatBlock,
  hiliteColor,
  horizontalRule,
  lineHeight,
  list,
  paragraphStyle,
  table,
  template,
  textStyle,
  image,
  link,
} from "suneditor/src/plugins";
import {
  FormControl,
  FormLabel,
  useColorModeValue,
  Text,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import IsLoading from "../IsLoading";
import { path } from "../../../utils/Api";
import { actions } from "../CustomUpload/redux/slice";

interface Props {
  name: string;
  value: string;
  handleChange: (name: string, value: string) => void;
  disabled?: boolean;
  placeholder: string;
  title: string;
  isRequiredFalse?: boolean;
}

const CustomSunEditor: React.FC<Props> = ({
  handleChange,
  name,
  placeholder,
  title,
  value,
  disabled,
  isRequiredFalse,
}) => {
  const textColor = useColorModeValue("navy.700", "white");
  const [showEditor, setShowEditor] = React.useState(true);
  const dispatch = useDispatch();

  React.useEffect(() => {
    const timer = setTimeout(() => setShowEditor(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // 🔁 1. Redux-powered function to get signed URL
  const getSignedUrl = (
    fileName: string
  ): Promise<{ url: string; prefix: string }> => {
    return new Promise((resolve, reject) => {
      try {
        dispatch(
          actions.getSignedUrl({
            fileName, // ✅ this becomes { fileName: "xyz.png" }
            callback: (url: string, prefix: string) => {
              resolve({ url, prefix });
            },
          })
        );
      } catch (error) {
        reject(error);
      }
    });
  };

  // 🗂 2. Upload file to pre-signed URL
  const uploadFile = (signedUrl: string, file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error("Failed to upload file to signed URL"));
        }
      };

      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.send(file);
    });
  };

  // 🖼 3. Hook into SunEditor's image upload
  const onImageUploadBefore = () => {
    return async (files: File[], info: any, uploadHandler: any) => {
      try {
        const file = files[0];
        const { url, prefix } = await getSignedUrl(file.name);
        await uploadFile(url, file);

        uploadHandler({
          result: [
            {
              url: `${path()}${prefix}`,
              name: file.name,
            },
          ],
        });
      } catch (error) {
        console.error("Image upload failed:", error);
        uploadHandler({
          errorMessage: "Image upload failed",
          result: [],
        });
      }
    };
  };

  return (
    <FormControl isRequired={!isRequiredFalse} mb="24px">
      <FormLabel
        display="flex"
        ms="4px"
        fontSize="sm"
        fontWeight="500"
        color={textColor}
        mb="8px"
      >
        {title}
      </FormLabel>

      {showEditor ? (
        <IsLoading />
      ) : (
        <>
          <SunEditor
            //@ts-ignore
            onImageUploadBefore={onImageUploadBefore()}
            setOptions={{
              showPathLabel: false,
              minHeight: "300px",
              maxHeight: "300px",
              placeholder: placeholder ?? "Enter your text here!!!",
              plugins: [
                align,
                font,
                fontColor,
                fontSize,
                formatBlock,
                hiliteColor,
                horizontalRule,
                lineHeight,
                list,
                paragraphStyle,
                table,
                template,
                textStyle,
                image,
                link,
              ],
              buttonList: [
                ["undo", "redo"],
                ["font", "fontSize", "formatBlock"],
                ["paragraphStyle"],
                [
                  "bold",
                  "underline",
                  "italic",
                  "strike",
                  "subscript",
                  "superscript",
                ],
                ["fontColor", "hiliteColor"],
                ["removeFormat"],
                ["codeView", "showBlocks"],
                "/",
                ["outdent", "indent"],
                ["align", "horizontalRule", "list", "lineHeight"],
                ["table", "link", "image"], // ensure image button is present
              ],
              formats: ["p", "div", "h1", "h2", "h3", "h4", "h5", "h6"],
              font: ["Arial"],
            }}
            onChange={(content: string) => handleChange(name, content)}
            defaultValue={value}
            disable={disabled}
          />
          <Text fontSize="xs" mt={2} color="gray.500">
            * Recommended image size: ≤ 50KB
          </Text>
        </>
      )}
    </FormControl>
  );
};

export default CustomSunEditor;
